import { useState, useEffect, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ticketsApi } from "../utils/api";

const tierColors = {
  blue:   { bg:"rgba(55,138,221,0.12)",  border:"rgba(55,138,221,0.5)",  dot:"#378ADD", badge:"rgba(55,138,221,0.2)",  text:"#7ec8ff" },
  gold:   { bg:"rgba(245,158,11,0.12)",  border:"rgba(245,158,11,0.5)",  dot:"#f59e0b", badge:"rgba(245,158,11,0.2)",  text:"#fcd34d" },
  purple: { bg:"rgba(155,63,232,0.12)",  border:"rgba(155,63,232,0.5)",  dot:"#9b3fe8", badge:"rgba(155,63,232,0.2)",  text:"#d49dff" },
  teal:   { bg:"rgba(29,158,117,0.12)",  border:"rgba(29,158,117,0.5)",  dot:"#1D9E75", badge:"rgba(29,158,117,0.2)",  text:"#6ee7b7" },
  red:    { bg:"rgba(239,68,68,0.12)",   border:"rgba(239,68,68,0.5)",   dot:"#ef4444", badge:"rgba(239,68,68,0.2)",   text:"#fca5a5" },
};

function useCountdown(dateStr) {
  const [time, setTime] = useState({d:0,h:0,m:0,s:0,over:false});
  useEffect(() => {
    const tick = () => {
      const diff = new Date(dateStr) - new Date();
      if (diff <= 0) { setTime({d:0,h:0,m:0,s:0,over:true}); return; }
      setTime({ d:Math.floor(diff/86400000), h:Math.floor((diff%86400000)/3600000), m:Math.floor((diff%3600000)/60000), s:Math.floor((diff%60000)/1000), over:false });
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [dateStr]);
  return time;
}

export default function EventDetailPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const bannerRef = useRef(null);
  const [event, setEvent]         = useState(null);
  const [loading, setLoading]     = useState(true);
  const [selectedTier, setSelectedTier] = useState(null);
  const [qty, setQty]             = useState(1);
  const [imgError, setImgError]   = useState(false);
  const [scrollY, setScrollY]     = useState(0);
  const [showShare, setShowShare] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', onScroll, { passive:true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    ticketsApi.getEvent(slug)
      .then(d => { setEvent(d.event); if (d.event.tiers?.length === 1) setSelectedTier(d.event.tiers[0].id); })
      .catch(() => setEvent(null))
      .finally(() => setLoading(false));
  }, [slug]);

  const countdown = useCountdown(event?.event_date);

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center" style={{background:"#07000f"}}>
      <div className="text-center">
        <div className="w-12 h-12 border-2 border-brand-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"/>
        <p className="text-gray-400 text-sm">Loading event...</p>
      </div>
    </div>
  );

  if (!event) return (
    <div className="min-h-screen flex items-center justify-center" style={{background:"#07000f"}}>
      <div className="text-center px-4">
        <p className="text-5xl mb-4">🎟️</p>
        <p className="font-display text-2xl text-white mb-3">Event not found</p>
        <Link to="/" className="text-brand-400 hover:text-brand-300 transition text-sm">← Back to events</Link>
      </div>
    </div>
  );

  const tier     = event.tiers?.find(t => t.id === selectedTier);
  const avail    = tier ? tier.capacity - tier.sold_count : 0;
  const subtotal = tier ? parseInt(tier.price) * qty : 0;

  return (
    <div className="min-h-screen" style={{background:"#07000f"}}>

      {/* ── HERO ── */}
      <div className="relative h-[55vh] md:h-[75vh] overflow-hidden" ref={bannerRef}>
        {event.banner_image && !imgError ? (
          <img src={event.banner_image} alt={event.title}
            className="w-full h-full object-cover"
            style={{transform:`translateY(${scrollY * 0.35}px) scale(1.1)`, transformOrigin:'center top'}}
            onError={() => setImgError(true)}/>
        ) : (
          <div className="w-full h-full" style={{background:"linear-gradient(135deg,#1a0030,#0d0020)"}}/>
        )}
        <div className="absolute inset-0" style={{background:"linear-gradient(to bottom,rgba(0,0,0,0.15) 0%,rgba(7,0,15,0.6) 60%,rgba(7,0,15,1) 100%)"}}/>

        {/* Back */}
        <div className="absolute top-20 left-4 z-10">
          <Link to="/" className="inline-flex items-center gap-1.5 glass text-white text-xs md:text-sm px-3 md:px-4 py-2 rounded-full hover:bg-white/10 transition">
            ← All Events
          </Link>
        </div>

        {/* Share button */}
        <div className="absolute top-20 right-4 z-10">
          <button onClick={() => setShowShare(!showShare)}
            className="glass text-white text-xs px-3 py-2 rounded-full hover:bg-white/10 transition">
            Share 🔗
          </button>
          {showShare && (
            <div className="absolute right-0 top-10 glass rounded-2xl p-3 flex flex-col gap-2 min-w-[160px] z-20">
              {[
                { label:"WhatsApp", color:"#25D366" },
                { label:"Facebook", color:"#1877F2" },
                { label:"Copy Link", color:"#9b3fe8" },
              ].map(s => (
                <button key={s.label} onClick={() => {
                  setShowShare(false);
                  if (s.label==="Copy Link") { navigator.clipboard.writeText(window.location.href); alert("Copied!"); }
                  else if (s.label==="WhatsApp") window.open(`https://wa.me/?text=${encodeURIComponent(event.title+' '+window.location.href)}`);
                  else window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`);
                }}
                  className="text-left text-xs px-3 py-2 rounded-xl transition hover:opacity-80 text-white font-medium"
                  style={{background:s.color+"33", border:`1px solid ${s.color}44`, color:s.color}}>
                  {s.label}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Hero content */}
        <div className="absolute bottom-0 left-0 right-0 p-4 md:p-8 z-10">
          <div className="max-w-6xl mx-auto">
            <div className="flex flex-wrap gap-2 mb-2 md:mb-3">
              <span className="glass text-white text-xs font-semibold px-2.5 py-1 rounded-full">{event.category}</span>
              {event.tags?.slice(0,3).map(t => <span key={t} className="glass text-gray-300 text-xs px-2.5 py-1 rounded-full">{t}</span>)}
            </div>
            <h1 className="font-display font-black text-white leading-tight mb-1 md:mb-2"
              style={{fontSize:"clamp(24px,5vw,56px)", textShadow:"0 2px 20px rgba(0,0,0,0.5)"}}>
              {event.title}
            </h1>
            {event.subtitle && <p className="text-gray-300 text-sm md:text-lg line-clamp-2">{event.subtitle}</p>}
          </div>
        </div>
      </div>

      {/* ── COUNTDOWN ── */}
      {countdown.over ? (
        <div style={{background:"rgba(34,197,94,0.08)", borderBottom:"1px solid rgba(34,197,94,0.15)"}}>
          <div className="max-w-6xl mx-auto px-4 py-3 flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse"/>
            <p className="text-green-400 text-sm font-medium">🎉 This event is happening now!</p>
          </div>
        </div>
      ) : (
        <div style={{background:"rgba(155,63,232,0.08)", borderBottom:"1px solid rgba(155,63,232,0.15)"}}>
          <div className="max-w-6xl mx-auto px-4 py-3 md:py-4 flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse"/>
              <p className="text-gray-400 text-xs md:text-sm font-medium">Event starts in</p>
            </div>
            <div className="flex items-center gap-1.5 md:gap-2">
              {[{v:countdown.d,l:"D"},{v:countdown.h,l:"H"},{v:countdown.m,l:"M"},{v:countdown.s,l:"S"}].map(({v,l},i) => (
                <div key={l} className="flex items-center gap-1.5">
                  <div className="text-center">
                    <div className="rounded-lg px-2 md:px-3 py-1.5 min-w-[40px] md:min-w-[52px]"
                      style={{background:"rgba(155,63,232,0.2)", border:"1px solid rgba(155,63,232,0.4)"}}>
                      <p className="text-white font-black text-lg md:text-2xl leading-none tabular-nums">{String(v).padStart(2,'0')}</p>
                    </div>
                    <p className="text-gray-600 text-xs mt-1">{l}</p>
                  </div>
                  {i < 3 && <p className="text-brand-500 font-bold text-lg mb-4">:</p>}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── MAIN ── */}
      <div className="max-w-6xl mx-auto px-4 py-6 md:py-10">
        <div className="grid lg:grid-cols-3 gap-6 md:gap-10">

          {/* Left */}
          <div className="lg:col-span-2 space-y-5 md:space-y-8">

            {/* Quick info */}
            <div className="grid grid-cols-3 gap-2 md:gap-4">
              {[
                { icon:"📅", label:"Date",  value: event.event_date ? new Date(event.event_date).toLocaleDateString('en-GB',{weekday:'short',month:'short',day:'numeric',year:'numeric'}) : '' },
                { icon:"🕐", label:"Time",  value: event.start_time ? event.start_time.slice(0,5) + (event.end_time ? ` — ${event.end_time.slice(0,5)}` : '') : '' },
                { icon:"📍", label:"Venue", value: event.secret_location ? "📍 Location revealed on your ticket" : `${event.venue_name}${event.city ? ', '+event.city : ''}` },
              ].map(info => (
                <div key={info.label} className="glass rounded-xl md:rounded-2xl p-3 md:p-5 text-center md:text-left">
                  <p className="text-xl md:text-2xl mb-1 md:mb-2">{info.icon}</p>
                  <p className="text-xs text-gray-500 uppercase tracking-wider mb-0.5 md:mb-1">{info.label}</p>
                  <p className="text-white font-semibold text-xs md:text-sm leading-snug">{info.value}</p>
                </div>
              ))}
            </div>

            {/* About */}
            {event.description && (
              <div className="glass rounded-2xl p-5 md:p-7">
                <h2 className="font-display text-lg md:text-2xl font-bold text-white mb-3 md:mb-4">About This Event</h2>
                <p className="text-gray-300 leading-relaxed text-sm md:text-base">{event.description}</p>
              </div>
            )}

            {/* Organiser */}
            <div className="glass rounded-xl md:rounded-2xl p-4 md:p-5 flex items-center gap-3 md:gap-4">
              <div className="w-10 h-10 md:w-12 md:h-12 rounded-xl flex items-center justify-center text-base md:text-lg font-black text-white flex-shrink-0"
                style={{background:"linear-gradient(135deg,#7c22c8,#9b3fe8)"}}>
                {event.organiser_name?.[0] || "N"}
              </div>
              <div className="flex-1">
                <p className="text-xs text-gray-500 mb-0.5">Organised by</p>
                <p className="text-white font-semibold text-sm md:text-base">{event.organiser_name || "NagNail Events"}</p>
              </div>
              <span className="flex items-center gap-1 text-xs text-green-400 font-medium flex-shrink-0">
                <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/></svg>
                Verified
              </span>
            </div>

            {/* Tiers */}
            <div>
              <h2 className="font-display text-lg md:text-2xl font-bold text-white mb-3 md:mb-5">Choose Your Ticket</h2>
              <div className="space-y-3">
                {event.tiers?.map(t => {
                  const c     = tierColors[t.color] || tierColors.blue;
                  const av    = t.capacity - t.sold_count;
                  const pct   = Math.round((t.sold_count / t.capacity) * 100);
                  const isSel = selectedTier === t.id;
                  const isOut = av <= 0;

                  return (
                    <button key={t.id} disabled={isOut}
                      onClick={() => { if (!isOut) { setSelectedTier(isSel ? null : t.id); setQty(1); }}}
                      className={`w-full text-left rounded-xl md:rounded-2xl p-4 md:p-6 border-2 transition-all duration-200 ${isOut?"opacity-40 cursor-not-allowed":"cursor-pointer hover:scale-[1.01]"}`}
                      style={{ background: isSel ? c.bg : "rgba(255,255,255,0.03)", borderColor: isSel ? c.dot : "rgba(255,255,255,0.08)" }}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{background:c.dot, boxShadow:`0 0 8px ${c.dot}`}}/>
                            <span className="font-bold text-white text-base md:text-lg">{t.name}</span>
                            {isOut && <span className="text-xs px-2 py-0.5 rounded-full font-medium" style={{background:"rgba(239,68,68,0.2)", color:"#fca5a5"}}>Sold Out</span>}
                            {!isOut && !isSel && <span className="text-xs px-2 py-0.5 rounded-full font-medium ml-auto" style={{background:"rgba(255,255,255,0.05)", color:"#9ca3af"}}>Tap to select</span>}
  
                          </div>

                          {t.perks?.length > 0 && (
                            <div className="flex flex-wrap gap-1.5 mb-3">
                              {t.perks.map(p => (
                                <span key={p} className="text-xs px-2 py-0.5 rounded-full" style={{background:c.badge, color:c.text}}>✓ {p}</span>
                              ))}
                            </div>
                          )}


                        </div>

                        <div className="text-right flex-shrink-0">
                          <p className="text-xs text-gray-500 mb-1">per ticket</p>
                          <p className="text-xl md:text-2xl font-black text-white">LKR {parseInt(t.price).toLocaleString()}</p>
                        </div>
                      </div>

                      {isSel && (
                        <div className="mt-4 pt-4 border-t border-white/10 flex items-center justify-between gap-4">
                          <p className="text-sm text-gray-300 font-medium">Qty</p>
                          <div className="flex items-center rounded-xl overflow-hidden" style={{background:"rgba(255,255,255,0.05)", border:"1px solid rgba(255,255,255,0.1)"}}>
                            <button onClick={e=>{e.stopPropagation();setQty(Math.max(1,qty-1));}}
                              className="w-10 h-10 text-white font-bold hover:bg-white/10 transition flex items-center justify-center">−</button>
                            <span className="text-white font-bold w-10 text-center">{qty}</span>
                            <button onClick={e=>{e.stopPropagation();setQty(Math.min(10,Math.min(av,qty+1)));}}
                              className="w-10 h-10 text-white font-bold hover:bg-white/10 transition flex items-center justify-center">+</button>
                          </div>
                          <div className="ml-auto text-right">
                            <p className="text-xs text-gray-500">Subtotal</p>
                            <p className="text-white font-black text-lg">LKR {(parseInt(t.price)*qty).toLocaleString()}</p>
                          </div>
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

          </div>

          {/* Right: Desktop sticky */}
          <div className="hidden lg:block lg:col-span-1">
            <div className="sticky top-24 space-y-4">
              <div className="rounded-3xl overflow-hidden" style={{background:"linear-gradient(135deg,rgba(124,34,200,0.15),rgba(155,63,232,0.05))", border:"1px solid rgba(155,63,232,0.25)"}}>
                <div className="p-6">
                  <h3 className="font-display text-xl font-bold text-white mb-5">Order Summary</h3>
                  {!tier ? (
                    <div className="text-center py-8">
                      <p className="text-4xl mb-3">🎫</p>
                      <p className="text-gray-400 text-sm">Select a ticket to continue</p>
                    </div>
                  ) : (
                    <>
                      <div className="space-y-3 mb-5 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-400">{tier.name}</span>
                          <span className="text-white font-semibold">LKR {parseInt(tier.price).toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-gray-400">Quantity</span>
                          <div className="flex items-center rounded-xl overflow-hidden" style={{background:"rgba(255,255,255,0.05)", border:"1px solid rgba(255,255,255,0.1)"}}>
                            <button onClick={()=>setQty(Math.max(1,qty-1))} className="w-8 h-8 text-white font-bold hover:bg-white/10 transition flex items-center justify-center text-lg">−</button>
                            <span className="text-white font-bold w-8 text-center text-sm">{qty}</span>
                            <button onClick={()=>setQty(Math.min(10,Math.min(avail,qty+1)))} className="w-8 h-8 text-white font-bold hover:bg-white/10 transition flex items-center justify-center text-lg">+</button>
                          </div>
                        </div>
                        <div className="flex justify-between pt-4 border-t border-white/10 text-base font-bold">
                          <span className="text-white">Total</span>
                          <span style={{background:"linear-gradient(135deg,#d49dff,#f59e0b)", WebkitBackgroundClip:"text", WebkitTextFillColor:"transparent"}}>
                            LKR {subtotal.toLocaleString()}
                          </span>
                        </div>
                      </div>

                      <button onClick={() => navigate(`/book/${slug}/${tier.id}/${qty}`)}
                        className="w-full py-4 rounded-2xl font-black text-white text-lg transition-all hover:scale-[1.02] active:scale-[0.98] mb-4 relative overflow-hidden"
                        style={{background:"linear-gradient(135deg,#166534,#16a34a)", boxShadow:"0 8px 32px rgba(22,163,74,0.4)"}}>
                        🎟️ Book Now
                      </button>

                      <div className="space-y-2 text-xs text-gray-500">
                        {[["🏦","Pay via bank transfer"],["📱","QR ticket on WhatsApp"],["⏱️","Confirmed within 2 hours"],["✅","No extra fees"]].map(([i,t]) => (
                          <div key={t} className="flex items-center gap-2"><span>{i}</span> {t}</div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
                <div className="h-1" style={{background:"linear-gradient(90deg,#7c22c8,#9b3fe8,#f59e0b)"}}/>
              </div>

              {/* Share */}
              <div className="glass rounded-2xl p-4">
                <p className="text-xs text-gray-500 mb-3 font-medium">Share this event</p>
                <div className="flex gap-2">
                  {[
                    { label:"WhatsApp", color:"#25D366", icon:"📱" },
                    { label:"Facebook", color:"#1877F2", icon:"📘" },
                    { label:"Copy",     color:"#6b7280", icon:"🔗" },
                  ].map(s => (
                    <button key={s.label} onClick={() => {
                      if (s.label==="Copy") { navigator.clipboard.writeText(window.location.href); alert("Copied!"); }
                      else if (s.label==="WhatsApp") window.open(`https://wa.me/?text=${encodeURIComponent(event.title+' '+window.location.href)}`);
                      else window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`);
                    }}
                      className="flex-1 text-xs py-2 rounded-xl font-medium transition hover:opacity-90"
                      style={{background:s.color+"22", border:`1px solid ${s.color}44`, color:s.color}}>
                      {s.icon} {s.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>

      {/* Mobile sticky bottom bar */}
      {tier && (
        <div className="lg:hidden fixed bottom-0 left-0 right-0 z-50 p-3"
          style={{background:"rgba(7,0,15,0.97)", backdropFilter:"blur(20px)", borderTop:"1px solid rgba(255,255,255,0.08)"}}>
          <div className="flex items-center gap-3 max-w-lg mx-auto">
            <div className="flex-1">
              <p className="text-gray-500 text-xs">{qty} ticket{qty>1?"s":""} · {tier.name}</p>
              <p className="text-white font-black text-lg leading-tight">LKR {subtotal.toLocaleString()}</p>
            </div>
            <button onClick={() => navigate(`/book/${slug}/${tier.id}/${qty}`)}
              className="px-6 py-3.5 rounded-2xl font-black text-white text-base transition-all active:scale-95 flex-shrink-0"
              style={{background:"linear-gradient(135deg,#166534,#16a34a)", boxShadow:"0 8px 24px rgba(22,163,74,0.4)"}}>
              Book Now 🎟️
            </button>
          </div>
        </div>
      )}

      {tier && <div className="h-24 lg:hidden"/>}
    </div>
  );
}
